import React, {useCallback, useEffect, useState} from 'react';
import http from "../../config/http-common";
import { Button, Modal, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import ProductComponent from "./ProductComponent";
import CatalogsModalV1 from "./CatalogsModalV1";
import { ErrorBoundary } from 'react-error-boundary';
import ProductModalV1 from "./ProductModalV1";
import discountTypesData from "../../resources/discount.json";
import {logout, UseRefreshToken} from "../../services/authService";


type Category = {
    Id: string,
    Name: string,
    Unit: string,
    Discount: string,
    Products: Product[] | null,
};

type Product = {
    ID: string,
    CategoryID: string,
    Name: string,
    SKU: string,
    ImageLink: string,
    Units: Unit[],
};

type Unit = {
    Id: string,
    Name: string,
    Price: number,
};

type ModalInfo = {
    show: boolean,
    mode: "edit" | "create"
};
type ProductModalInfo = {
    show: boolean,
    editMode: boolean
}

const Catalog: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true); // add loading state

    const [showProductModal, setShowProductModal] = useState<ProductModalInfo>({show: false, editMode: false});
    const [modalInfo, setModalInfo] = useState<ModalInfo>({ show: false, mode: 'create' });
    const [categoryList, setCategoryList] = useState<Category[] | null>(null); // set initial state to null
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [products, setProducts] = useState<Product[] | null>(null);
    const renderKey = useCallback(() => Math.random().toString(), []);
    const handleShowCatalogsModalClose = () => {setModalInfo({ show: false, mode: 'create' });};
    const handleShowProductModalClose = () => setShowProductModal({show:false, editMode:false});
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

    const discountTypes = discountTypesData;
    const getDiscountType = useCallback(
        (id: string) => {
            const discount = discountTypes.find((discount) => discount.discountId === id);
            return discount ? discount.discountType : undefined;
        },
        [discountTypes]
    );
    const fetchCatalogList = useCallback(async () => {
        setIsLoading(true); // set loading state to true before fetch
        const token = localStorage.getItem('token');

        await http.get('/api/category', {
            headers: {
                'Token': token
            }
        }).then(response => {
            setCategoryList(Array.isArray(response.data) ? response.data : null); // checking if response data is array before setting it
        }).catch((error: { response: { status: number; }; }) => {

            if (error.response) {
                const status = error.response.status;
                if (status === 401) {
                    UseRefreshToken().then(() => {
                        fetchCatalogList();
                    });
                }
            } else {
                logout();
            }
        }).finally(() => {
            setIsLoading(false); // set loading state to false after fetch
        });
    }, []);

    useEffect(() => {
        fetchCatalogList();
    }, [fetchCatalogList, navigate]);

    const handleCategoryDelete = async (id: string) => {
        const token = localStorage.getItem('token');
        try {
            await http.delete('/api/category', {
                headers: {
                    'Token': token
                },
                params: { categoryID: id }
            });
            if(categoryList) {
                setCategoryList(categoryList.filter(category => category.Id !== id));
            }
        } catch (error) {
            logout();
        }
    };

    const askDeleteConfirmation = () => {
        setShowDeleteConfirmationModal(true);
    };

    const handleConfirmDelete = () => {
        if (selectedCategory) { // Check that selectedCategory is not null
            handleCategoryDelete(selectedCategory.Id);
            setShowDeleteConfirmationModal(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirmationModal(false);
    };

    const handleNewCatalogs = () => {
        setModalInfo({ ...modalInfo, show: true, mode: 'create' });
    };

    const handleEditCatalogs = (event: React.MouseEvent) => {
        event.stopPropagation(); // stops the event from propagating to the outer elements
        setModalInfo({ ...modalInfo, show: true, mode: 'edit' });
    };

    const handleOnSuccess = () => {
        //reload catalog list after successful operation
        fetchCatalogList();
    };

    const handleNewProduct = () => {
        setShowProductModal({show:true, editMode:false})
    };

    const handleCategoryClick = async (id: string) => {
        // Toggle behavior: if clicking the already selected category, collapse it
        if (selectedCategory?.Id === id) {
            setSelectedCategory(null);
            setProducts(null);
            setShowDeleteConfirmationModal(false);
            return;
        }

        await refreshProductsForCategory(id, true);
    };

    // Refresh products for a category without toggling close when the same category is selected
    const refreshProductsForCategory = async (id: string, updateSelection: boolean = false) => {
        setIsLoading(true);
        if (categoryList) {
            const selectedCat = categoryList.find(category => category.Id === id) || null;

            if (updateSelection) {
                setSelectedCategory(selectedCat);
            } else {
                // Ensure selection persists; if nothing selected, set it
                if (!selectedCategory || selectedCategory.Id !== id) {
                    setSelectedCategory(selectedCat);
                }
            }

            setProducts(null);
            const token = localStorage.getItem('token');
            await http.get('/api/product', {
                headers: {
                    'Token': token
                },
                params: { categoryID: selectedCat?.Id }
            }).then((response) => {
                setProducts(response.data || null);
            }).catch((error: { response: { status: number; }; }) => {
                if (error.response) {
                    const status = error.response.status;
                    if (status === 401) {
                        UseRefreshToken().then(() => {
                            refreshProductsForCategory(id, updateSelection);
                        });
                    }
                } else {
                    navigate('/login');
                }
            }).finally(() => {
                setIsLoading(false);
            });
        }
    };

    return (
        <ErrorBoundary FallbackComponent={() => <div>Error occurred, redirecting...</div>} onReset={() => navigate('/login')}>
        <div className="container mt-4" key={renderKey()}>

            {/* Add absolute-centered spinner for loading state */}
            {isLoading && (
                <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}>
                    <Spinner animation="border" />
                </div>
            )}

            <div className="row align-items-center mb-3">
                <div className="col-md-6 col-12">
                    <h2 className="mb-0">Katalogs</h2>
                </div>
                <div className="col-md-6 col-12 text-md-end mt-2 mt-md-0">
                    <div className="btn-group" role="group" aria-label="Augšējā darbību josla">
                        <button
                            className="btn btn-outline-primary shadow-sm me-2"
                            onClick={() => handleNewCatalogs()}
                            title="Pievienot jaunu kategoriju"
                        >
                            <i className="bi bi-folder-plus me-1"></i>
                            Jauna kategorija
                        </button>
                    </div>
                </div>
            </div>

            <div className="row my-2">

                    {/* The list of catalogs */}
                {categoryList && categoryList.length > 0 && categoryList.map((catalog, index) => (

                    <>
                    <div className="col-12" key={catalog.Id}>
                        {/* Wrapper with border and rounded corners around category and its products */}
                        <div className={`border rounded-3 p-2 mb-3 ${selectedCategory?.Id === catalog.Id ? 'border-success-subtle' : 'border-secondary-subtle'}`}>
                            <div key={index} className="col-12">
                                <div role="button" className={`card shadow-sm mb-2`} onClick={() => handleCategoryClick(catalog.Id)}>
                                    <h5 className={`card-header bg-white fw-semibold ${selectedCategory?.Id === catalog.Id ? 'text-success border-success' : ''}`}>{catalog.Name}</h5>

                                <div className="card-body pt-2">
                                    <div className="row">
                                        <div className="col-lg-5 col-12">
                                            <div className="col">Mērvienība: <b>{catalog.Unit}</b></div>
                                        </div>
                                        <div className="col-lg-5 col-12">
                                            <div className="col">Atlaides
                                                veids: <b>{getDiscountType(catalog.Discount)}</b></div>
                                        </div>
                                        <div className="col-lg-2 col-12 text-end">
                                            {selectedCategory?.Id === catalog.Id && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm me-2"
                                                        onClick={(e) => { e.stopPropagation(); handleNewProduct(); }}
                                                        title="Pievienot jaunu produktu šajā kategorijā"
                                                    >
                                                        <i className="bi bi-plus-circle me-1"></i>
                                                        Jauns Produkts
                                                    </button>
                                                    <i className="bi bi-pencil m-1" onClick={handleEditCatalogs}></i>
                                                    {products == null && (
                                                        <i className="bi bi-x-lg m-1" onClick={(e) => { e.stopPropagation(); askDeleteConfirmation(); }}></i>
                                                    )}
                                                </>
                                            )}

                                            <Modal show={showDeleteConfirmationModal} onHide={handleCancelDelete}>
                                                <Modal.Header closeButton>
                                                    <Modal.Title>Dzēšanas apstiprinājums</Modal.Title>
                                                </Modal.Header>
                                                <Modal.Body>Vai vēlaties dzēst izvēlēto Kategoriju?</Modal.Body>
                                                <Modal.Footer>
                                                    <Button variant="secondary" onClick={handleCancelDelete}>Nē</Button>
                                                    <Button variant="primary" onClick={handleConfirmDelete}>Jā</Button>
                                                </Modal.Footer>
                                            </Modal>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>

                            {/* Products section inside the same wrapper to keep a single rounded frame */}
                            <div className="col-12">
                                <hr className="mt-3 mb-0 opacity-25"/>
                                <div className="row">
                                    <div className="col-12 d-flex align-items-center">
                                        <div className="row w-100">
                                            {selectedCategory?.Id === catalog.Id && products && !isLoading && products.map((product, index) => (
                                                <ProductComponent product={product} categoryUnit={selectedCategory?.Unit || ''}
                                                                  handleCategoryClick={handleCategoryClick}
                                                                  refreshProductsForCategory={refreshProductsForCategory}
                                                                  key={index}/>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </>
                ))}


            </div>

            <CatalogsModalV1 key={renderKey()} show={modalInfo.show} mode={modalInfo.mode} onClick={handleShowCatalogsModalClose} onSuccess={handleOnSuccess} selectedCategory={selectedCategory!} />



            <ProductModalV1
                productToEdit={null}
                editMode={false}
                show={showProductModal.show}
                key={renderKey()}
                selectedCategoryUnit={selectedCategory?.Unit || ''}
                selectedCategoryId={selectedCategory?.Id || ''}
                closeHandler={handleShowProductModalClose}
                reloadCatalog={() => selectedCategory?.Id ? refreshProductsForCategory(selectedCategory.Id) : undefined as any}
            />
        </div>
        </ErrorBoundary>
    );
};

export default Catalog;